import React, { useEffect, useState } from "react";
import { Button, Popover, Box, Typography, FormControl, InputLabel, Select, MenuItem, Hidden } from "@material-ui/core";
import { getProfileContactData } from "api/user";
import { Club } from "api/clubs";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { CheckInInfo, getCheckInsSingleClub, patchCheckInsEnd, postCheckIns } from "api/checkins";
import { useSnackbar } from "notistack";
import { getPersistedData, PersistedData, setPersistedData } from "utils/persist";
import EditContactDataDialog from "features/user/ProfileView/EditContactDataDialog";
import i18n from "../i18n";

// prop types
interface CheckinPopoverProps {
    club: Club,
}

// checkin popover
const CheckinPopover = ({ club }: CheckinPopoverProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>();
    const [checkinInfo, setCheckinInfo] = useState<CheckInInfo>();
    const [durationMins, setDurationMins] = useState(60);
    const [isLoading, setLoading] = useState(false);
    const [showEditContactDataDialog, setShowEditContactDataDialog] = useState(false);

    // checkin action
    const checkinAction = async () => {
        setLoading(true);
        try {
            const contactData = await getProfileContactData();
            await postCheckIns({
                clientLatitude: club!.latitude,
                clientLongitude: club!.longitude,
                clubId: club!.id,
                contactData: {
                    ...contactData!,
                    consentDate: moment().toISOString(),
                    storePermanently: false,
                },
                durationMins,
                guestIdentifier: getPersistedData(PersistedData.GuestIdentifier)!,
            });
            fetchCurrentCheckin();
        } catch (error: any) {
            if (error?.type === "contactDataNotFound" || error?.type === "invalidParameters") {
                enqueueSnackbar(i18n.completeContactData, { variant: "info" });
                setShowEditContactDataDialog(true);
            } else {
                displayError(error);
            }
        }
        setLoading(false);
    };

    // checkout action
    const checkoutAction = async () => {
        setLoading(true);
        try {
            await patchCheckInsEnd({
                checkInId: checkinInfo!.activeCheckInId!,
                guestIdentifier: getPersistedData(PersistedData.GuestIdentifier)!,
            });
            fetchCurrentCheckin();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch current checkin if exists
    const fetchCurrentCheckin = async () => {
        // get or create guest identifier
        let guestIdentifier = getPersistedData(PersistedData.GuestIdentifier);
        if (!guestIdentifier) {
            guestIdentifier = uuidv4();
            setPersistedData(PersistedData.GuestIdentifier, guestIdentifier);
        }
        setCheckinInfo(await getCheckInsSingleClub({ clubId: club!.id, guestIdentifier }));
    };
    useEffect(() => { if (club) fetchCurrentCheckin(); }, [club]);

    // render
    return (
        <>
            {/* button */}
            <Hidden xsDown>
                <Button color="primary" style={checkinInfo?.activeCheckInId ? { marginLeft: 8, color: "#d44336" } : { marginLeft: 8 }} onClick={(e) => setAnchorEl(e.currentTarget)}>
                    {checkinInfo?.activeCheckInId ? i18n.checkOut : i18n.checkIn}
                </Button>
            </Hidden>

            {/* popover */}
            <Popover
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={!!anchorEl}
                PaperProps={{ elevation: 4, style: { minWidth: "320px", display: "flex", flexDirection: "column", alignItems: "center" } }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                onClose={() => setAnchorEl(null)}
            >
                {checkinInfo?.activeCheckInId ? (
                    <>
                        {/* currently checked-out */}
                        <Typography style={{ marginTop: "24px", width: "100%", padding: "0 24px" }} align="center" variant="body2">
                            {i18n.activeCheckIn(checkinInfo.club.clubName, moment(checkinInfo.userIsCheckedInUntilDate).format("H:mm"))}
                        </Typography>
                        <Button disabled={isLoading} variant="contained" style={{ background: "#d44336", color: "#fff", marginTop: "16px", marginBottom: "24px" }} onClick={checkoutAction}>
                            {i18n.checkOut}
                        </Button>
                    </>
                ) : (
                    <>
                        {/* currently checked-in */}
                        <Box style={{ marginTop: "24px", width: "100%", padding: "0 24px" }}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>{i18n.duration}</InputLabel>
                                <Select
                                    label={i18n.duration}
                                    value={durationMins}
                                    onChange={(e) => setDurationMins(parseInt(e.target.value as string, 10))}
                                >
                                    {[30, 60, 90, 120, 360, 720].map((d) => (
                                        <MenuItem key={d} value={d}>{i18n.hours(d / 60)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Button color="primary" disabled={isLoading} variant="contained" style={{ marginTop: "16px", marginBottom: "24px" }} onClick={checkinAction}>
                            {i18n.checkIn}
                        </Button>
                    </>
                )}
            </Popover>

            {/* edit contact details dialog */}
            <EditContactDataDialog show={showEditContactDataDialog} onClose={() => setShowEditContactDataDialog(false)} />
        </>
    );
};

export default CheckinPopover;
